import { useEffect, useState } from "react";
import { Link, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Menu, LogOut, RefreshCw, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RT_NAV, ADMIN_NAV, dtNav, PANEL_NAME, NETWORK_ROLES, allowedNav, adminNavFor } from "@/nav";
import { Agreement } from "@/pages/Distributor";
import NotificationBell from "@/components/NotificationBell";
import { ShubhPayMark } from "@/components/Logo";
import { api, inr } from "@/lib/api";

function Sidebar({ nav, onNavigate }) {
  const loc = useLocation();
  const isActive = (to) => to === loc.pathname + loc.search || (!loc.search && to === loc.pathname);
  return (
    <nav className="flex-1 overflow-y-auto px-3 pb-6" data-testid="sidebar-nav">
      {nav.map((g) => (
        <div key={g.group} className="mt-5">
          <div className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{g.group}</div>
          {g.items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              onClick={onNavigate}
              data-testid={`nav-${it.id}`}
              className={`group flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors ${
                isActive(it.to) ? "bg-emerald-500/15 text-emerald-300" : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <it.icon className="h-4 w-4 shrink-0 opacity-80" />
              {it.label}
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );
}

function WalletChip({ label, value, tone, testid }) {
  return (
    <div className={`rounded-lg px-3 py-1.5 text-white ${tone}`} data-testid={testid}>
      <div className="text-[9px] font-semibold uppercase tracking-widest opacity-80">{label}</div>
      <div className="font-mono text-sm font-semibold leading-tight">{inr(value)}</div>
    </div>
  );
}

const MOCK_PATHS = ["/recharge", "/bbps", "/credit-card", "/aeps", "/aadhaar-pay", "/cash-deposit", "/pan-verify", "/qr-collect", "/payment-gateway"];

export default function AppLayout() {
  const loc = useLocation();
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  const { user, wallet, logout, refreshBalance, reloadUser } = useAuth();
  useEffect(() => { reloadUser().catch(() => {}); }, [loc.pathname]); // eslint-disable-line
  useEffect(() => { const t = setInterval(() => reloadUser().catch(() => {}), 30000); return () => clearInterval(t); }, []); // eslint-disable-line
  useEffect(() => { document.title = user.branding?.company_name || "ShubhPay B2B"; return () => { document.title = "ShubhPay"; }; }, [user.branding?.company_name]);
  const [open, setOpen] = useState(false);
  const [spin, setSpin] = useState(false);
  const nav = useNavigate();
  const isAdmin = user.role === "admin" || user.role === "subadmin";
  const isDt = NETWORK_ROLES.includes(user.role);
  const items = isAdmin ? adminNavFor(user) : allowedNav(isDt ? dtNav(user.role) : RT_NAV, user.services);
  const blocked = user.role === "subadmin" ? ADMIN_NAV.flatMap((g) => g.items).some((it) => it.perm && it.to !== "/admin" && loc.pathname.startsWith(it.to) && !adminNavFor(user).some((g) => g.items.some((x) => x.to === it.to))) && loc.pathname !== "/admin" : !isAdmin && (isDt ? dtNav(user.role) : RT_NAV).flatMap((g) => g.items).some((it) => it.svc && user.services?.[it.svc] === false && it.to.split("?")[0] === loc.pathname);

  const refresh = async () => {
    setSpin(true);
    await refreshBalance();
    setTimeout(() => setSpin(false), 500);
  };

  const brand = (
    <div className="flex items-center gap-2 px-6 pt-6">
      {user.branding ? (
        <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${user.branding.logo_url}`} alt="logo" className="h-9 max-w-[64px] rounded-md bg-white object-contain p-0.5" data-testid="brand-logo" />
      ) : (
        <ShubhPayMark className="h-9 w-9" />
      )}
      <div className="min-w-0">
        <div className="truncate font-display text-[15px] font-bold leading-none text-white" data-testid="brand-name">{user.branding?.company_name || "ShubhPay"}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">{isAdmin ? "Admin Hub" : isDt ? `${PANEL_NAME[user.role]} Panel` : "Retailer Panel"}</div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col bg-[#0B1220] lg:flex">
        {brand}
        <Sidebar nav={items} />
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <aside className="flex h-full w-72 flex-col bg-[#0B1220]">
            <div className="flex items-center justify-between pr-4">{brand}<button onClick={() => setOpen(false)} className="mt-6 text-slate-300" data-testid="close-mobile-nav"><X /></button></div>
            <Sidebar nav={items} onNavigate={() => setOpen(false)} />
          </aside>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/85 px-4 py-2.5 backdrop-blur-xl sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-mobile-nav"><Menu className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold" data-testid="header-user-name">{user.name}</div>
            <div className="truncate text-[11px] text-slate-500">
              {user.login_id} · {user.mobile} {user.shop_name ? `· ${user.shop_name}` : ""}
              <span className="ml-2 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">{user.post_id || "RT"} · KYC {user.kyc_status}</span>
            </div>
          </div>
          {!isAdmin && (
            <div className="hidden items-center gap-2 sm:flex">
              <WalletChip label="Main Wallet" value={wallet.balance} tone="bg-gradient-to-r from-[#0D1B3E] to-[#122452]" testid="main-wallet-balance" />
              {!isDt && <WalletChip label="AEPS Wallet" value={wallet.aeps_balance} tone="bg-gradient-to-r from-emerald-600 to-teal-700" testid="aeps-wallet-balance" />}
              <button onClick={refresh} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" data-testid="refresh-balance-button" title="Refresh balance">
                <RefreshCw className={`h-4 w-4 ${spin ? "animate-spin" : ""}`} />
              </button>
            </div>
          )}
          {!isAdmin && (
            <NotificationBell />
          )}
          <button onClick={async () => { await logout(); nav("/login"); }} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold hover:bg-slate-50" data-testid="logout-button">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </header>
        {!isAdmin && (
          <div className="flex gap-2 border-b border-slate-200 bg-white px-4 py-2 sm:hidden">
            <WalletChip label="Main" value={wallet.balance} tone="flex-1 bg-gradient-to-r from-[#0D1B3E] to-[#122452]" testid="main-wallet-balance-mobile" />
            {!isDt && <WalletChip label="AEPS" value={wallet.aeps_balance} tone="flex-1 bg-gradient-to-r from-emerald-600 to-teal-700" testid="aeps-wallet-balance-mobile" />}
          </div>
        )}
        <main className="mx-auto w-full max-w-[1400px] flex-1 p-4 sm:p-6 lg:p-8">
          {!isAdmin && !isDt && meta && !meta.live_services && MOCK_PATHS.some((p) => loc.pathname.startsWith(p)) && (
            <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="service-coming-soon-banner">
              This service is not live yet and will be activated soon. Move to Bank, fund requests and reports are available.
            </div>
          )}
          {isDt && !user.agreement_signed ? <Agreement /> : blocked ? <Navigate to={isAdmin ? "/admin" : "/"} replace /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
